import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { AttributeContainer, AttributeName, AttributeValue, Column, CrossContainer, HeaderContainer, InputsContainer } from './styles';
import { CircularProgress, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import theme from '../../theme/theme';
import { AddCircle, Close, Edit } from '@mui/icons-material';
import { postLocation } from '../../services/locations/postLocations';
import { RequestModal } from '../../components/RequestModal';
import { LocationActionsProps } from './types';
import { putLocation } from '../../services/locations/putLocation';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    minWidth: '380px',
    maxHeight: '700px',
    overflow: 'auto',
    borderRadius: '20px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const LocationActions: React.FC<LocationActionsProps> = ({ action, locationObj, onResponse }) => {
    // ------------------- JWT -------------------
    const jwt = localStorage.getItem('jwt');

    // ------ Modal ------
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setIsPosted(false);
        if (action !== 'edit') {
            setLocationName('');
            setBuilding('');
            setFloor('');
            setRoom('');
            setDescription('');
        }
    }

    // ------ States ------
    const [locationName, setLocationName] = React.useState(action === 'create' ? '' : locationObj.name);
    const [building, setBuilding] = React.useState(action === 'create' ? '' : locationObj.building);
    const [floor, setFloor] = React.useState(action === 'create' ? '' : locationObj.floor);
    const [room, setRoom] = React.useState(action === 'create' ? '' : locationObj.room);
    const [description, setDescription] = React.useState(action === 'create' ? '' : locationObj.description);

    const [isPosted, setIsPosted] = React.useState(false);
    const [isInvalid, setIsInvalid] = React.useState(false);
    const [warningMessage, setWarningMessage] = React.useState("");

    const [loadingAction, setLoadingAction] = React.useState(false);

    const [locationError, setLocationError] = React.useState(false);
    const [buildingError, setBuildingError] = React.useState(false);
    const [floorError, setFloorError] = React.useState(false);
    const [roomError, setRoomError] = React.useState(false);
    const [descriptionError, setDescriptionError] = React.useState(false);

    // ------ Requests ------
    const handleLocation = async () => {
        if (!locationName || !building || !floor || !room || !description) {
            setWarningMessage("Por favor, complete todos los campos.");
            setIsInvalid(true);
        } else {
            const location = {
                name: locationName,
                building: building,
                floor: floor,
                room: room,
                description: description
            }
            if (action === 'create') {
                setLoadingAction(true);
                await postLocation(jwt!, location)
                    .then((response) => {
                        setWarningMessage("La locación ha sido creada exitosamente.");
                        setIsPosted(true);
                        onResponse && onResponse();
                    })
                    .catch((error) => {
                        console.log(error);
                        setWarningMessage("Error al crear la locación. Por favor, inténtelo de nuevo.");
                        setIsInvalid(true);
                    })
                setLoadingAction(false);
            } else {
                setLoadingAction(true);
                await putLocation(jwt!, locationObj.id, location)
                    .then((response) => {
                        setWarningMessage("La ubicación ha sido modificada exitosamente.");
                        setIsPosted(true);
                        onResponse && onResponse();
                    })
                    .catch((error) => {
                        console.log(error);
                        setWarningMessage("Error al efectuar la operación. Por favor, inténtelo de nuevo.");
                        setIsInvalid(true);
                    })
                setLoadingAction(false);
            }
        }
    }

    // ------ Handlers ------
    const handleLocationName = (e: any) => {
        setLocationName(e.target.value);
    }

    const handleLocationBlur = (e: any) => {
        if (!e.target.value) {
            setLocationError(true);
        } else {
            setLocationError(false);
        }
    }

    const handleBuilding = (e: any) => {
        setBuilding(e.target.value);
    }

    const handleBuildingBlur = (e: any) => {
        if (!e.target.value) {
            setBuildingError(true);
        } else {
            setBuildingError(false);
        }
    }

    const handleFloor = (e: any) => {
        setFloor(e.target.value);
    }

    const handleFloorBlur = (e: any) => {
        if (!e.target.value) {
            setFloorError(true);
        } else {
            setFloorError(false);
        }
    }

    const handleRoom = (e: any) => {
        setRoom(e.target.value);
    }

    const handleRoomBlur = (e: any) => {
        if (!e.target.value) {
            setRoomError(true);
        } else {
            setRoomError(false);
        }
    }

    const handleDescription = (e: any) => {
        setDescription(e.target.value);
    }

    const handleDescriptionBlur = (e: any) => {
        if (!e.target.value) {
            setDescriptionError(true);
        } else {
            setDescriptionError(false);
        }
    }

    // ------ Effects ------


    return (
        <div>
            {
                isPosted ? (
                    <RequestModal
                        title='¡Locación creada!'
                        description={warningMessage}
                        onConfirm={handleClose}
                    />
                ) : (
                    isInvalid &&
                    <RequestModal
                        title='Ocurrió un error'
                        description={warningMessage}
                        onConfirm={(!locationName || !building || !floor || !room || !description) ?
                            () => { setIsInvalid(false) } : handleClose
                        }
                    />
                )
            }
            <Button onClick={handleOpen} style={{ padding: '0px' }} >
                {action === 'create' ?
                    <AddCircle sx={{ color: theme.color.navy, fontSize: '2rem' }} />
                    :
                    <Edit sx={{ color: theme.color.navy, fontSize: '2rem' }} />
                }
            </Button>
            <Modal
                open={open}
                // onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    {
                        loadingAction ? (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <CircularProgress sx={{ color: theme.color.navy }} />
                            </div>
                        ) : (
                            <>
                                <CrossContainer
                                    onClick={handleClose}
                                >
                                    <Close />
                                </CrossContainer>
                                <HeaderContainer>
                                    {
                                        action === 'create' ?
                                            "Agregar nueva ubicación"
                                            :
                                            "Editar ubicación"
                                    }
                                </HeaderContainer>
                                <InputsContainer>
                                    <Column>
                                        <AttributeContainer>
                                            <TextField
                                                label="Nombre de locación"
                                                type="text"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                sx={{ margin: "15px 0", width: '100%' }}
                                                InputProps={{
                                                    style: {
                                                        borderRadius: "14px",
                                                    },
                                                }}
                                                value={locationName}
                                                onChange={handleLocationName}
                                                onBlur={handleLocationBlur}
                                                error={locationError}
                                                helperText={locationError ? 'Este campo es obligatorio' : ''}
                                            />
                                        </AttributeContainer>
                                        <AttributeContainer>
                                            <TextField
                                                label="Edificio"
                                                type="text"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                sx={{ margin: "15px 0", width: '100%' }}
                                                InputProps={{
                                                    style: {
                                                        borderRadius: "14px",
                                                    },
                                                }}
                                                value={building}
                                                onChange={handleBuilding}
                                                onBlur={handleBuildingBlur}
                                                error={buildingError}
                                                helperText={buildingError ? 'Este campo es obligatorio' : ''}
                                            />
                                        </AttributeContainer>
                                        <AttributeContainer>
                                            <TextField
                                                label="Piso"
                                                type="text"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                sx={{ margin: "15px 0", width: '100%' }}
                                                InputProps={{
                                                    style: {
                                                        borderRadius: "14px",
                                                    },
                                                }}
                                                value={floor}
                                                onChange={handleFloor}
                                                onBlur={handleFloorBlur}
                                                error={floorError}
                                                helperText={floorError ? 'Este campo es obligatorio' : ''}
                                            />
                                        </AttributeContainer>
                                    </Column>
                                    <Column>
                                        <AttributeContainer>
                                            <TextField
                                                label="Salón"
                                                type="text"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                sx={{ margin: "15px 0", width: '100%' }}
                                                InputProps={{
                                                    style: {
                                                        borderRadius: "14px",
                                                    },
                                                }}
                                                value={room}
                                                onChange={handleRoom}
                                                onBlur={handleRoomBlur}
                                                error={roomError}
                                                helperText={roomError ? 'Este campo es obligatorio' : ''}
                                            />
                                        </AttributeContainer>
                                        <AttributeContainer>
                                            <TextField
                                                label="Descripción"
                                                type="text"
                                                variant="outlined"
                                                fullWidth
                                                multiline
                                                maxRows={4}
                                                required
                                                sx={{ margin: "15px 0", width: '100%' }}
                                                InputProps={{
                                                    style: {
                                                        borderRadius: "14px",
                                                    },
                                                }}
                                                value={description}
                                                onChange={handleDescription}
                                                onBlur={handleDescriptionBlur}
                                                error={descriptionError}
                                                helperText={descriptionError ? 'Este campo es obligatorio' : ''}
                                            />
                                        </AttributeContainer>
                                    </Column>
                                </InputsContainer>
                                <Button
                                    onClick={handleLocation}
                                    variant="contained"
                                    sx={{
                                        width: "100%",
                                        marginTop: "20px",
                                        borderRadius: "15px",
                                        fontSize: "16px",
                                        backgroundColor: theme.color.navy,
                                        color: theme.color.white,
                                        textTransform: "none",
                                        "&:hover": {
                                            backgroundColor: theme.color.space,
                                        },
                                    }}
                                >
                                    Guardar
                                </Button>
                            </>
                        )
                    }
                </Box>
            </Modal>
        </div>
    );
}

export default LocationActions;